import React, { useState, useEffect, useMemo } from "react";
import { Tabs, Tab, Box, TextField } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DetailedAnalyticsBrandPulseNew } from "../../common/analytics/detailed-analytics-new/src";
import { BrandPulseProfileCard } from "./brand-pulse-profile-card";
import MaterialSymbol from "../../../components/icons/material-symbol";
import CommonPopUp from "../../../components/popup/common-popup";
import { AnalyticsService } from "../../../core/services/analytics.service";
import { ApplyCampaignService, LocalService } from "../../../core/services";
import { WhatsappIcon, WhatsappShareButton } from "react-share";
import { createPortal } from "react-dom";
import MuiTooltip from "@mui/material/Tooltip";
import * as gtUtil from "../../../core/utility";

interface Props {
  linkedCampaignId: any[];
  loader?:boolean;
  profileData?:any;
  // opens the parent's Create Campaign popup - without it a brand that already has
  // campaigns had no way to add another one
  onCreateCampaign?: () => void;
  // asks the parent to re-fetch the campaign list after a campaign is deleted
  onCampaignDeleted?: () => void;
}

const analyticsService = new AnalyticsService();
const localService = new LocalService();

export const BrandPulseCampaignList: React.FC<Props> = ({ linkedCampaignId, loader, profileData, onCreateCampaign, onCampaignDeleted }) => {

  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [openInitalSharePopup, setOpenInitalSharePopup] = useState(false);
  const [shareURL,setShareURL] = useState("");
  const [openAddUser, setOpenAddUser] = useState(false)
  const [openWhatsAppPopup, setOpenWhatsAppPopup] = useState(false)
  // Rename (pencil icon on a campaign chip). Renamed titles are kept locally so the chip
  // updates without re-fetching the list - a refetch would reset the selected tab.
  const [titleOverrides, setTitleOverrides] = useState<Record<number, string>>({});
  const [editCampaign, setEditCampaign] = useState<{ campaign_id: number; title: string } | null>(null);
  const [savingTitle, setSavingTitle] = useState(false);
  const [editError, setEditError] = useState("");
  // Delete (bin icon on a campaign chip) - confirmed through a warning popup first
  const [deleteCampaign, setDeleteCampaign] = useState<{ campaign_id: number; title: string } | null>(null);
  const [deletingCampaign, setDeletingCampaign] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  // Deleted chips are hidden right away; the parent's re-fetch (heavy profileCard call,
  // ~3-4s) only confirms it in the background instead of the chip lingering until then.
  const [removedIds, setRemovedIds] = useState<number[]>([]);
  const campaigns = useMemo(
    () => (linkedCampaignId || []).filter((c: any) => !removedIds.includes(c.campaign_id)),
    [linkedCampaignId, removedIds]
  );

  const getTitle = (c: any) => titleOverrides[c?.campaign_id] ?? c?.campaign_title;

  const openEditTitle = (event: React.MouseEvent, c: any) => {
    event.stopPropagation(); // don't let the click also switch tabs
    setEditError("");
    setEditCampaign({ campaign_id: c.campaign_id, title: getTitle(c) || "" });
  };

  const handleSaveTitle = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!editCampaign) return;
    const title = editCampaign.title.trim();
    if (!title) {
      setEditError("Campaign name is required.");
      return;
    }
    try {
      setSavingTitle(true);
      const res = await analyticsService.updateBrandPulseCampaignTitle({
        campaign_id: editCampaign.campaign_id,
        campaign_title: title,
      });
      if (res?.status === gtUtil.Constants.SUCCESS) {
        setTitleOverrides((prev) => ({ ...prev, [editCampaign.campaign_id]: title }));
        localService.toastify("Campaign name updated", "success", 2000);
        setEditCampaign(null);
      } else {
        setEditError(res?.info || "Could not update the campaign name, please try again.");
      }
    } catch (err) {
      setEditError("Could not update the campaign name, please try again.");
    } finally {
      setSavingTitle(false);
    }
  };

  const openDeleteCampaign = (event: React.MouseEvent, c: any) => {
    event.stopPropagation(); // don't let the click also switch tabs
    setDeleteError("");
    setDeleteCampaign({ campaign_id: c.campaign_id, title: getTitle(c) || "" });
  };

  const handleDeleteCampaign = async () => {
    if (!deleteCampaign) return;
    try {
      setDeletingCampaign(true);
      const res = await analyticsService.deleteBrandPulseCampaign({ campaign_id: deleteCampaign.campaign_id });
      if (res?.status === gtUtil.Constants.SUCCESS) {
        localService.toastify("Campaign deleted", "success", 2000);
        setRemovedIds((prev) => [...prev, deleteCampaign.campaign_id]);
        setDeleteCampaign(null);
        onCampaignDeleted?.();
      } else {
        setDeleteError(res?.info || "Could not delete the campaign, please try again.");
      }
    } catch (err) {
      setDeleteError("Could not delete the campaign, please try again.");
    } finally {
      setDeletingCampaign(false);
    }
  };

  const openInitialSharePopup = () => {
    setOpenInitalSharePopup(true);
  };

  const closeInitialSharePopup = () => {
    setOpenInitalSharePopup(false);
  };

  const openAddShareUser = () => {
    setOpenAddUser(true);
  };

  const closeWhatsAppSharePopup = () => {
    setOpenWhatsAppPopup(false);
  };

  // ✅ Set default tab
  // OLD: always jumped to the first campaign, so deleting another chip lost the user's tab
  // useEffect(() => {
  //   if (linkedCampaignId?.length > 0) {
  //     setCampaignId(linkedCampaignId[0].campaign_id);
  //   }
  // }, [linkedCampaignId]);
  useEffect(() => {
    if (campaigns.length > 0) {
      setCampaignId((prev) =>
        campaigns.some((c: any) => c.campaign_id === prev) ? prev : campaigns[0].campaign_id
      );
    } else {
      setCampaignId(null); // last campaign deleted
    }
  }, [campaigns]);

  useEffect(() => {
      const fetchShareURL = async () => {
        try {
          const res = await analyticsService.brandPulseAnalyticsSharePublicLink({
            campaign_id: campaignId,
            ref_id: profileData[0]?.ref_id || null,
            username: profileData[0]?.username || null,
          });
  
          if (res?.data?.status) {
            const url = res?.data?.url;
            setShareURL(url || ""); // Update the URL state
          } else {
            console.error("Error fetching URL:", res?.data?.message);
          }
        } catch (error) {
          console.error("Error while fetching WhatsApp share URL:", error);
        }
      };
  
      fetchShareURL(); // Call the async function
    }, [campaignId, profileData]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setCampaignId(newValue);
  };

   const handleCopyShareLink = async () =>{
    if (!campaignId || !profileData || profileData.length === 0) {
    console.error("Invalid campaign ID or profile data.");
    return;
  }

  try {
    const res = await analyticsService.brandPulseAnalyticsSharePublicLink({
      campaign_id: campaignId,
      ref_id: profileData[0]?.ref_id || null,
      username: profileData[0]?.username || null,
    });

    if (res.data.status) {
      const url = res.data.url;
      setShareURL(url); // Update the URL state
      navigator.clipboard.writeText(url);
      localService.toastify('Link Copied', 'success', 1000);
    } else {
      console.error("Error fetching URL:", res.data.message);
    }
  } catch (error) {
    console.error("Error while fetching WhatsApp share URL:", error);
  }
    
  }

  return (
    <div className="mx-auto mt-2 position-relative">
     
      <div className="mt-5 mb-5 ">
        <BrandPulseProfileCard loader={!!loader} profileData={profileData} />
      </div>
      {/* ✅ Scrollable Tabs */}
      <div className="d-flex align-items-center mt-2 mb-4">
      <Tabs
        value={campaignId}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        // className="nav sub-tabs nav-rounded mt-2 mb-4"
        className="nav sub-tabs nav-rounded"
        sx={{ minWidth: 0 }}
      >
        {campaigns.map((data: any, index:any) => (
          <Tab
            key={data.campaign_id}
            // OLD: label={data.campaign_title}
            label={
              <span className="d-inline-flex align-items-center">
                {getTitle(data)}
                <MuiTooltip title="Edit campaign name" arrow placement="top">
                  <span
                    role="button"
                    aria-label="Edit campaign name"
                    className="brand-pulse-campaign-edit-icon ms-2 d-inline-flex"
                    onClick={(e) => openEditTitle(e, data)}
                  >
                    <MaterialSymbol iconName="edit" fontweight="200" fontSize="1rem" lineHeight="1" />
                  </span>
                </MuiTooltip>
                <MuiTooltip title="Delete campaign" arrow placement="top">
                  <span
                    role="button"
                    aria-label="Delete campaign"
                    className="brand-pulse-campaign-edit-icon brand-pulse-campaign-delete-icon ms-1 d-inline-flex"
                    onClick={(e) => openDeleteCampaign(e, data)}
                  >
                    <MaterialSymbol iconName="delete" fontweight="200" fontSize="1rem" lineHeight="1" />
                  </span>
                </MuiTooltip>
              </span>
            }
            value={data.campaign_id}
            className={`${index === data.campaign_id ? "nav-link active" : "nav-link"}`}
            
          />
        ))}
      </Tabs>
      {/* outside <Tabs> so it stays visible when the campaign row scrolls */}
      {onCreateCampaign && (
        <MuiTooltip title="Create Campaign" arrow placement="top">
          <button
            type="button"
            className="create-campaign-icon-btn flex-shrink-0"
            aria-label="Create Campaign"
            onClick={onCreateCampaign}
          >
            <MaterialSymbol iconName="add" fontweight="200" fontSize="1.25rem" lineHeight="1" />
          </button>
        </MuiTooltip>
      )}
      </div>

      <div className="d-flex justify-content-end">
        <span
          className="cursor-pointer btn btn-primary ms-1 d-flex align-items-center"
          onClick={openInitialSharePopup}
        >
          <MaterialSymbol
            className="me-1"
            iconName="share"
            fontweight="200"
            fontSize="18px"
          />
          SHARE
        </span>
      </div>

      {/* ✅ Tab Content */}
      <Box mt={3}>
        {campaignId && (
          <DetailedAnalyticsBrandPulseNew
            key={campaignId}   // force remount
            linkedCampaignId={campaignId}
            // OLD: campaignTitle={linkedCampaignId?.find((c: any) => c.campaign_id === campaignId)?.campaign_title}
            campaignTitle={getTitle(campaigns.find((c: any) => c.campaign_id === campaignId))}
          />
        )}
      </Box>
{createPortal(
  <CommonPopUp 
        className="modal-medium share-container"
          openModal={openInitalSharePopup}
          modalTitle="Share"
          onClose={closeInitialSharePopup}
      >
        <div className="analytic-share-option d-flex align-items-center mb-3">
          <strong className="analytic-share-option-title">Share via:</strong>
          <div className="analytic-share-option-container">
            <button disabled onClick={openAddShareUser} className="btn btn-outline-primary d-inline-flex">
              <MaterialSymbol iconName="email" fontweight="200" />
              Email
            </button>
            <span className="btn btn-outline-primary d-inline-flex react-share-btn">
            <WhatsappShareButton
                url={shareURL || ""}
                className='Demo__some-network__share-button'
                disabled={!shareURL}
                onClick={() => {
                console.log("WhatsApp share clicked");
              }}
              >
                <WhatsappIcon style={{ color: '#25D366' }} />
                <span className='share-titles'>WhatsApp</span>
              </WhatsappShareButton>
            </span>
            
            <button onClick={handleCopyShareLink} className="btn btn-outline-primary d-inline-flex">
              <MaterialSymbol iconName="link" fontweight="200" />              
              Copy Link
            </button>
          </div>
        </div>
        </CommonPopUp> , document.body)} 

{editCampaign && createPortal(
  <CommonPopUp
        className="modal-medium create-brand-pulse-campaign"
        openModal={!!editCampaign}
        modalTitle="Edit Campaign Name"
        onClose={() => setEditCampaign(null)}
      >
        <form onSubmit={handleSaveTitle}>
          <TextField
            className="w-100 input-common mb-3"
            size="small"
            label="Campaign Name"
            variant="outlined"
            autoFocus
            value={editCampaign.title}
            inputProps={{ maxLength: 255 }}
            onChange={(e) => {
              setEditCampaign({ ...editCampaign, title: e.target.value });
              if (editError) setEditError("");
            }}
          />
          <div className="d-flex align-items-center gap-2">
            <LoadingButton className="btn btn-primary btn-mui" variant="contained" type="submit" loading={savingTitle}>
              Save
            </LoadingButton>
            <button type="button" className="btn btn-outline-primary btn-mui" onClick={() => setEditCampaign(null)}>
              Cancel
            </button>
          </div>
          {editError && <span className="fs-14 d-block mt-2 error">{editError}</span>}
        </form>
      </CommonPopUp>
      , document.body)}

{deleteCampaign && createPortal(
  <CommonPopUp
        className="modal-medium create-brand-pulse-campaign"
        openModal={!!deleteCampaign}
        modalTitle="Delete Campaign"
        onClose={() => !deletingCampaign && setDeleteCampaign(null)}
      >
        <div className="d-flex align-items-start gap-2 mb-3">
          <MaterialSymbol iconName="warning" fontweight="300" fontSize="1.5rem" color="#d32f2f" />
          <p className="mb-0">
            Are you sure you want to delete <strong>{deleteCampaign.title}</strong>? All posts tracked
            in this campaign will be removed and its analytics will no longer be available. This action
            cannot be undone.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <LoadingButton
            className="btn btn-primary btn-mui"
            variant="contained"
            color="error"
            loading={deletingCampaign}
            onClick={handleDeleteCampaign}
          >
            Delete
          </LoadingButton>
          <button
            type="button"
            className="btn btn-outline-primary btn-mui"
            disabled={deletingCampaign}
            onClick={() => setDeleteCampaign(null)}
          >
            Cancel
          </button>
        </div>
        {deleteError && <span className="fs-14 d-block mt-2 error">{deleteError}</span>}
      </CommonPopUp>
      , document.body)}

{createPortal(
  <CommonPopUp 
        className="modal-medium share-container"
          openModal={openWhatsAppPopup}
          modalTitle="Share"
          onClose={closeWhatsAppSharePopup}
      >
        <strong className="d-block mb-2">Share via</strong>
        <div className="d-flex gap-3 mb-3">
            <WhatsappShareButton
              url={shareURL}
              className='Demo__some-network__share-button'
            >
              <WhatsappIcon style={{ color: '#25D366' }} />
              <span className='share-titles'>WhatsApp</span>
            </WhatsappShareButton>
        </div>
          
        
        </CommonPopUp>
         , document.body)} 

    </div>
  );
};
